import {ComboboxItem} from "../../_inputs/ha-combobox/combobox-item.interface";
import {IssueType} from "../../../domain/entities/issue-type.entity";
import {offerableTypesOf, rootUidOf} from "../../../domain/helpers/issue-type-root.helper";
import {isUnsavedType} from "../../../helpers/loaded-issue-types.helper";

/**
 * Whether the issue's type still has to be chosen, so no save path may send it: the type is abstract
 * (the placeholder a new absence starts on when its root offers several children, initialTypeFor) — the
 * backend refuses an abstract type — or there is no type at all (the save transformer reads
 * issue.type.uid, and a live reload of the picker can empty a value that is not in its list).
 */
export function isTypeChoicePending(type: IssueType | null | undefined): boolean {
    return !type || type.isAbstract;
}

const familyFilters = new Map<string, (item: ComboboxItem) => boolean>();

/**
 * The type picker's item filter for one root (D56): the root itself unless abstract, and its children;
 * types not saved yet are left out, as in the menu and the create actions. With `keepAbstractRoot` the
 * abstract root stays in the list too: while it is the placeholder value, a live reload of the picker
 * would otherwise find the value missing from the list and clear it. The combobox already leaves deleted
 * types out. One stable function per root and variant, because Lit's property binding compares by
 * reference and a new function on every render would look like a change.
 */
export function typeFamilyItemFilter(rootUid: string, keepAbstractRoot: boolean): (item: ComboboxItem) => boolean {
    const key = `${rootUid}|${keepAbstractRoot}`;
    let filter = familyFilters.get(key);
    if (!filter) {
        filter = (item: ComboboxItem) => {
            const type = item.payload as IssueType | undefined;
            if (!type || isUnsavedType(type)) {
                return false;
            }
            if (type.uid === rootUid) {
                return keepAbstractRoot || !type.isAbstract;
            }
            return type.parentUid === rootUid && !type.isAbstract;
        };
        familyFilters.set(key, filter);
    }
    return filter;
}

/**
 * Whether the dialog shows the type row, for which root, and whether its list keeps the abstract root
 * (see typeFamilyItemFilter): shown only where the issue's family offers more than one type (D7), or
 * while the type is an abstract placeholder that must be replaced. A type without a known root (and no
 * type) shows no row.
 */
export function typePickerState(type: IssueType | null | undefined, types: readonly IssueType[]): {
    visible: boolean,
    rootUid: string | null,
    keepAbstractRoot: boolean
} {
    const rootUid = rootUidOf(type);
    if (!rootUid) {
        return {visible: false, rootUid: null, keepAbstractRoot: false};
    }
    const placeholder = type!.isAbstract;
    return {visible: placeholder || offerableTypesOf(rootUid, types).length > 1, rootUid, keepAbstractRoot: placeholder};
}
